import { useCallback, useState } from 'react';
import { useQuery } from '@apollo/client';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { Text } from 'react-aria-components';
import PageTitle from 'src/components/creator/PageTitle';
import OutOfPrintTable from 'src/components/creator/OutOfPrintTable';
import TextField from 'src/components/ui/TextField';
import IconButton from 'src/components/ui/IconButton';
import { useDebouncedCallback } from 'use-debounce';
import { iconStyles } from 'src/components/ui/Field';
import CloseIcon from 'src/assets/icons/close.svg';
import SEARCH_PUBLICATIONS_QUERY from 'src/graphql/query/search-publications.graphql';

const OutOfPrintPage = () => {
  const { term = '' } = Route.useSearch();
  const navigate = Route.useNavigate();
  const validQuery = term.length > 2;

  const { loading, data, called } = useQuery(SEARCH_PUBLICATIONS_QUERY, {
    variables: {
      page: { offset: 0, limit: 1000 },
      searchTerm: term,
    },
    skip: !validQuery,
  });

  const search = useCallback(
    (newTerm: string) => {
      void navigate({
        search: (prev) => ({
          ...prev,
          term: newTerm,
        }),
      });
    },
    [navigate]
  );

  const debouncedSearch = useDebouncedCallback(
    (text: string) => search(text),
    500
  );

  return (
    <>
      <PageTitle title="Out-of-Print Library" />

      <div className="bg-surface-alt rounded-xl shadow-3 mt-6 py-6 px-4 flex-1 min-h-0">
        <SearchField defaultTerm={term} onChange={debouncedSearch} />
        {validQuery && called ? (
          <div className="h-[calc(100%-5rem)]">
            <OutOfPrintTable
              aria-label="All Publications"
              rows={data?.publications ?? []}
              loading={loading}
            />
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center">
            <div className="w-full max-w-[31.25rem] text-surface-alt-90 text-center">
              <Text elementType="p" className="font-medium text-base mt-4 mb-6">
                Explore LAD’s Out-of-Print Library to discover hard-to-find
                titles. To begin your search, enter the book title, ISBN, or
                author information in the search field. Review the available
                listings, then place your order directly through the Library
                interface.
                <br />
                <br />
                If you do not find the desired title, we encourage you to submit
                a request with us directly. Our team will work to secure the
                necessary permissions and explore availability on your behalf.
              </Text>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const SearchField = ({
  defaultTerm = '',
  onChange,
}: {
  defaultTerm?: string;
  onChange: (text: string) => void;
}) => {
  const [termValue, setTermValue] = useState(defaultTerm);

  return (
    <TextField
      className="w-[26rem] mx-auto mb-6"
      // This search-only page has one primary task; focus the query field so users can begin immediately.
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus
      aria-label="Find Publications"
      placeholder="Title, Authors or ISBN"
      value={termValue}
      onChange={(value) => {
        setTermValue(value);
        onChange(value);
      }}
      description="Please type at least 3 characters..."
      rightSlot={
        Boolean(termValue.length) && (
          <IconButton
            aria-label="Clear search"
            className={iconStyles({
              className: 'right-0.5 !bg-transparent',
              size: 'medium',
            })}
            onPress={() => {
              setTermValue('');
              onChange('');
            }}
            Icon={CloseIcon}
          />
        )
      }
    />
  );
};

const PublicationsSchema = z.object({
  term: z.string().optional(),
});

export const Route = createFileRoute('/_authenticated/_creator/out-of-print/')({
  validateSearch: (search) => PublicationsSchema.parse(search),
  component: OutOfPrintPage,
});
